import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Button, Card, Chip, Dialog, EmptyState, Skeleton, toast } from '@/ui';
import { serverNow } from '@/lib/clock-skew';
import { totalAmount } from '@/lib/money';
import { pairOf } from '@/lib/task-currency';
import { marketplaceKeys } from '@/lib/task-query-keys';
import { filtersToServerParams, listTabHref } from '@/lib/task-list-url';
import { useServerList } from '@/lib/task-server-list';
import type { PhaseInput } from '@/lib/task-phase';
import { isActiveAcceptedStatus } from '@/lib/task-status';
import { useServerTick } from '@/lib/task-tick';
import { isCustomerOrgAdmin } from '@/auth/types';
import { useSessionStore } from '@/auth/session.store';
import { useCachedTask, useTask, useUpdateTask } from '@/features/tasks/queries';
import { sameId, type OfferRow, type TaskRow } from '@/features/tasks/types';
import { useUrlList } from '@/features/tasks/useUrlList';
import {
  ListEmpty,
  ListError,
  ListSkeleton,
  Pager,
} from '@/features/tasks/components/ListStates';
import { PaymentDueNotice } from '@/features/tasks/components/PaymentDue';
import { InfoGrid, QuoteCard, QuoteHeader } from '@/features/tasks/components/QuoteCard';
import { useCountdown } from '@/features/tasks/components/useCountdown';
import { taskErrorMessage } from '@/features/tasks/error-message';
import { fetchOfferPage } from './api';
import { CompactOfferRow, type OfferSelection } from './CompactOfferRow';
import {
  LIVE_POLL_MS,
  isLiveTask,
  offerListSort,
  phaseInputOf,
  requestsTabFor,
  selectionGate,
  splitWinner,
  taskFromOffer,
  type SelectionGate,
} from './request-detail';
import styles from './RequestOffers.module.css';

/**
 * TALEP DETAYI — bir talebe gelen teklifler; kazanan burada secilir (2026-10-02, "Alternatif A").
 *
 * ============================ TEK BASLIK, KOMPAKT SATIR =====================
 * Talep basligi (tutar, cift, yon, #id/tarih, faz + sayac) ustte BIR KEZ cizilir; teklifler
 * altinda kompakt satirlar (buro, birim kur, toplam, secim). Eskiden her teklif karti ayni
 * basligi tekrar ediyordu. Ic sekmeler (Hepsi / Onaylanan) KALDIRILDI: tek liste, kazanan
 * karar verilince EN USTE sabitlenir (ayri sorgu; liste sayfalanmis, kazanan ikinci sayfada
 * olabilir), kaybedenler solar.
 *
 * Baslik TALEP satirindan gelir (/v1/tasks?ids=): teklif fazinda henuz teklif yokken de
 * gorunmeli. Talep satiri alinamazsa ilk tekliften kurulur (eski davranis).
 *
 * ============================== FILTRE YOK ==================================
 * Liste TEK GOREVE filtreli; anlamli olan tek sey SIRALAMA. Varsayilan YONE GORE en iyi once
 * (musteri aliyorsa en dusuk kur, satiyorsa en yuksek); cipler bunu degistirir, ayni cipe tekrar
 * basmak varsayilana dondurur. Anahtarlar sunucunun sortMap'iyle birebir.
 *
 * ====================== CANLI LISTE, SECIM KARAR FAZINDA (M2) ===============
 * Teklifler TEKLIF FAZINDA da gorunur (2026-10-02; 2026-09-23'teki "karar Q2" kurali sahibine
 * teklif fazinda hicbir teklif gostermiyordu, KALKTI). Talep canliyken (teklif/karar fazinda
 * 'new') liste ve talep satiri 5 sn'de bir yoklanir — web'e push yok; yoklama yalniz sayfa
 * gorunurken. SECIM yine yalniz KARAR fazinda: sunucu oncesini 409 OFFER_WINDOW_OPEN ile
 * reddeder. Teklif fazinda satirlar SECILEMEZ ve bunu dugme gibi gorunmeyen bir sayacla
 * soyler; secim istemci sayaci karar fazina gectikten 2 sn sonra acilir (saat sapmasi payi,
 * request-detail.ts) ve ustte "Teklif suresi doldu — secim yapabilirsiniz" notu cikar.
 *
 * "Gorev karara baglandi mi" GOREVE ait (`pretenderUserId`); talep satirindan, o yoksa ilk teklif
 * satirindan okunur — tum listeyi tarayan bir kontrol sayfalanmis listede calisamaz.
 *
 * ============================== ODEME UYARISI ================================
 * 2026-09-26: talep 'pending' ve odeme penceresi aciksa ustte kirmizi bir not (kalan sureyle).
 * Musteri Taleplerim'deki "Odeme bilgileri" ile buraya geliyor; buronun IBAN'i kazanan satirda
 * ve kazanan zaten en ustte sabit — notun ayrica bir dugmeye ihtiyaci yok.
 */

/** Sekme YOK (2026-10-02); useUrlList yalniz siralama ve ofseti adres cubugunda tasiyor. */
const DEFAULT_TAB = 'all';

const OFFER_SORTS = [
  { value: 'offerPrice:asc', labelKey: 'filter.sortPriceAsc' },
  { value: 'offerPrice:desc', labelKey: 'filter.sortPriceDesc' },
  { value: 'offerCreatedAt:desc', labelKey: 'filter.sortOfferNewest' },
] as const;

/** Buronun adimlari ("Odeme alindi", "Islemi tamamla") web'e push ile gelmiyor — Taleplerim ile ayni. */
const ACCEPTED_POLL_MS = 15_000;

/**
 * Secim istemci sayacina gore acildiktan sonra gelen 409 OFFER_WINDOW_OPEN, "teklifler hala
 * toplaniyor" degil: sunucu saati birkac saniye geride. Genel metin yaniltirdi.
 */
const SELECTION_ERROR_KEYS: Readonly<Record<string, string>> = Object.freeze({
  OFFER_WINDOW_OPEN: 'task.selection.openingSoon',
});

const noop = (): void => undefined;

/** Talep satirinin yoklama araligi — SON GELEN satira gore (useTask). */
const taskPollMs = (task: TaskRow | null | undefined): number | false => {
  if (!task) return false;
  const now = serverNow();
  if (isLiveTask(phaseInputOf(task, now), now)) return LIVE_POLL_MS;
  return isActiveAcceptedStatus(task.taskStatusCode) ? ACCEPTED_POLL_MS : false;
};

/** Secim kapisi, saniyede bir — useCountdown ile ayni desen (kapali kapi abone olmaz). */
function useSelectionGate(input: PhaseInput): SelectionGate {
  const idle = selectionGate(input, useServerTick(false));
  const nowMs = useServerTick(idle.state !== 'closed');
  return selectionGate(input, nowMs);
}

export function RequestOffersPage() {
  const { t } = useTranslation();
  const params = useParams<{ taskId: string }>();
  const taskId = params.taskId ?? '';

  const url = useUrlList(DEFAULT_TAB);
  const snapshot = useSessionStore((state) => state.snapshot);
  const userId = snapshot?.user.id;
  const orgAdmin = isCustomerOrgAdmin(snapshot);

  const [selected, setSelected] = useState<OfferRow | null>(null);
  const updateTask = useUpdateTask();

  // --- Talep ------------------------------------------------------------------
  // Taleplerim'den gelindiyse satir onbellekte: baslik ANINDA, sorgu yine sunucuya gider.
  const seed = useCachedTask(taskId);
  // Taleplerim ile ayni sahiplik parametresi; sunucu degeri dogrulanmis cagirana zorlar ve
  // musteri org yoneticisine org'un taleplerini acar.
  const ownership = useMemo(() => (userId ? { taskUserId: userId } : {}), [userId]);
  const taskQuery = useTask(taskId, seed, ownership, taskPollMs);
  /** undefined: henuz yok; null: bulunamadi. */
  const taskRow = taskQuery.data;
  // Ilk denemesi biten (basarili ya da hatali) talep — teklif listesi bunu bekler.
  const taskSettled = taskRow !== undefined || taskQuery.failureCount > 0;

  const liveNowMs = useServerTick(taskRow?.taskStatusCode === 'new');
  const live = taskRow ? isLiveTask(phaseInputOf(taskRow, taskQuery.dataUpdatedAt), liveNowMs) : false;

  // --- Teklifler --------------------------------------------------------------
  const sort = offerListSort(url.filters.sort, taskRow?.taskIsBuying);
  const listParams = useMemo(
    () => ({ taskId, ...filtersToServerParams({ ...url.filters, sort }) }),
    [taskId, url.filters, sort],
  );

  const list = useServerList<OfferRow>({
    baseKey: marketplaceKeys.offerLists(),
    params: listParams,
    fetchPage: fetchOfferPage,
    offset: url.offset,
    onParamsChanged: url.resetOffset,
    // Talep cozulene kadar BEKLER: varsayilan siralama YONE bagli. Once yonsuz cekip sonra
    // yeniden siralamak hem iki istek hem de en kotu teklifin bir an en ustte gorunmesi demekti.
    enabled: taskId.length > 0 && taskSettled,
    pollMs: live ? LIVE_POLL_MS : undefined,
  });

  // Ust kart: talep satiri; o yoksa ilk tekliften.
  const first = list.items[0];
  const task: TaskRow | null = taskRow ?? (first ? taskFromOffer(first) : null);
  const phaseInput: PhaseInput = task
    ? phaseInputOf(task, taskRow ? taskQuery.dataUpdatedAt : list.receivedAtMs)
    : { receivedAtMs: 0 };
  const phase = useCountdown(phaseInput);
  const gate = useSelectionGate(phaseInput);

  const decided = task?.pretenderUserId != null;

  // Kazanan AYRI sorgudan: liste fiyata gore sirali ve sayfalanmis, kazanan baska sayfada olabilir.
  const winnerParams = useMemo(() => ({ taskId, offerStatus: 'approved' }), [taskId]);
  const winnerList = useServerList<OfferRow>({
    baseKey: marketplaceKeys.offerLists(),
    params: winnerParams,
    fetchPage: fetchOfferPage,
    offset: 0,
    onParamsChanged: noop,
    enabled: taskId.length > 0 && decided,
  });
  const { winner, others } = splitWinner(
    list.items,
    winnerList.items.find((offer) => offer.isAccepted),
    decided,
  );

  // SAHIPLIK: musteri org yoneticisi org'un taleplerine gelen teklifleri gorur, ama kazanani
  // YALNIZ talebi acan kisi secer — sunucu da postUpdateTask'i cagiranin kendi talebine
  // kelepceli tutuyor. Secim gosterilmez; neden gosterilmedigi ustteki notta.
  const taskOwnerId = task?.taskUserId ?? null;
  const isTaskOwner = taskOwnerId === null || sameId(taskOwnerId, userId);
  const ownerName =
    task && orgAdmin && !isTaskOwner ? (task.userFullName ?? t('task.unknownOwner')) : undefined;

  // Odeme uyarisi YALNIZ talep sahibine: metin "onayladiginiz teklif" diyor (Taleplerim ile ayni).
  const paymentDue = isTaskOwner && task?.taskStatusCode === 'pending' && phase.phase === 'payment';

  const selection: OfferSelection | null =
    !isTaskOwner || decided
      ? null
      : gate.state === 'open'
        ? { kind: 'open' }
        : gate.state === 'waiting'
          ? { kind: 'waiting', opensInMs: gate.opensInMs }
          : null;

  // Secim neden yok / ne zaman var? Sessiz kalmak "dugme bozuk" diye okunurdu. Teklif fazinda
  // not YOK: her satir kalan sureyi kendisi soyluyor.
  const hasOffers = list.items.length > 0 || winner !== null;
  const notice: { tone: 'info' | 'success'; text: string } | null =
    !hasOffers || decided
      ? null
      : !isTaskOwner
        ? { tone: 'info', text: t('task.ownerOnlySelection') }
        : gate.state === 'open'
          ? { tone: 'success', text: t('task.selection.open') }
          : gate.state === 'closed'
            ? { tone: 'info', text: t('task.selectionWindowClosed') }
            : null;

  const retry = (): void => {
    void taskQuery.refetch();
    list.refetch();
  };

  const pair = pairOf(task?.taskTypeCode);

  const sortBar = (
    <div className={styles.sortBar}>
      {OFFER_SORTS.map((option) => {
        // Etkin siralama vurgulanir — varsayilan (yone gore en iyi) dahil: liste nasil
        // sirali, kullanici gormeli.
        const active = sort === option.value;
        return (
          <button
            key={option.value}
            type="button"
            className={styles.sortButton}
            aria-pressed={active}
            onClick={() => {
              // Secili cipe tekrar basmak siralamayi KALDIRIR (yone gore varsayilana doner).
              url.setFilters({
                ...url.filters,
                sort: url.filters.sort === option.value ? '' : option.value,
              });
            }}
          >
            <Chip tone={active ? 'primary' : 'neutral'} size="sm">
              {t(option.labelKey)}
            </Chip>
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <section className={styles.page}>
        <h1 className={styles.title}>{t('task.offersTitle')}</h1>

        {/* Talebin bulundugu Taleplerim sekmesine doner (sekme eslemesinin aynasi). */}
        <Link
          className={styles.backLink}
          to={listTabHref('/musteri/talepler', requestsTabFor(task, phase.phase), 'openRequests')}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          {t('task.backToRequests')}
        </Link>

        {paymentDue ? <PaymentDueNotice remainingMs={phase.remainingMs} /> : null}

        {task === null ? (
          !taskSettled || list.isLoading ? (
            <Skeleton shape="block" height="132px" />
          ) : list.isError ? (
            <ListError error={list.error} onRetry={retry} />
          ) : (
            <Card padding="lg">
              <EmptyState
                tone="error"
                title={t('task.notFound')}
                description={t('task.notFoundDetail')}
                action={
                  <Button tone="neutral" onClick={retry}>
                    {t('common.retry')}
                  </Button>
                }
              />
            </Card>
          )
        ) : (
          <>
            <QuoteCard phase={phase}>
              <QuoteHeader
                id={task.id}
                taskTypeCode={task.taskTypeCode}
                isBuying={task.taskIsBuying}
                amount={totalAmount(task.taskAmount)}
                amountCurrency={pair.currency}
                createdAt={task.createdAt}
                phase={phase}
                warning={task.taskStatusCode === 'warning'}
                statusCode={task.taskStatusCode}
              />
              {ownerName === undefined ? null : (
                <InfoGrid items={[{ label: t('task.owner'), text: ownerName }]} />
              )}
            </QuoteCard>

            {notice === null ? null : (
              <p
                className={notice.tone === 'success' ? styles.banner : styles.notice}
                role={notice.tone === 'success' ? 'status' : undefined}
              >
                {notice.text}
              </p>
            )}

            {winner === null ? null : <CompactOfferRow offer={winner} phase={phase} won />}

            {others.length > 0 ? sortBar : null}

            {list.isLoading ? (
              <ListSkeleton rows={3} />
            ) : list.isError ? (
              <ListError error={list.error} onRetry={list.refetch} />
            ) : others.length === 0 ? (
              // Yalniz kazanan varsa "kayit yok" demek yanlis olurdu.
              winner === null ? (
                <ListEmpty description={live ? t('task.offersLiveEmpty') : undefined} />
              ) : null
            ) : (
              <ul className={styles.list}>
                {others.map((offer) => (
                  <li key={offer.id}>
                    <CompactOfferRow
                      offer={offer}
                      phase={phase}
                      lost={decided}
                      selection={selection}
                      onSelect={() => {
                        setSelected(offer);
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}

            <Pager
              offset={list.offset}
              pageSize={list.pageSize}
              returned={list.items.length}
              hasMore={list.hasMore}
              busy={list.isFetching}
              onOffsetChange={url.setOffset}
            />
          </>
        )}
      </section>

      <Dialog
        open={selected !== null}
        title={t('task.areYouSure')}
        description={t('task.acceptOfferDetail')}
        confirmLabel={t('actions.confirm')}
        cancelLabel={t('actions.cancel')}
        busy={updateTask.isPending}
        onCancel={() => {
          setSelected(null);
        }}
        onConfirm={() => {
          if (!selected || !userId) return;
          // Diyalog acikken karar suresi dolmus olabilir; istegi atip ret beklemek yerine kapat.
          if (gate.state !== 'open') {
            setSelected(null);
            toast({
              message:
                gate.state === 'waiting'
                  ? t('task.selection.openingSoon')
                  : t('task.selectionWindowClosed'),
              tone: 'danger',
            });
            return;
          }
          updateTask.mutate(
            {
              taskId: selected.taskId,
              taskStatus: 'pending',
              // KAZANAN BURONUN kimligi — teklifi verenin, oturumdakinin degil.
              taskPretenderUserId: selected.offerUserId,
            },
            {
              onSuccess: () => {
                setSelected(null);
                toast({ message: t('task.offerAccepted'), tone: 'success' });
              },
              onError: (error) => {
                setSelected(null);
                toast({
                  message: taskErrorMessage(error, t, undefined, SELECTION_ERROR_KEYS),
                  tone: 'danger',
                });
              },
            },
          );
        }}
      />
    </>
  );
}
